import { useState } from 'react';
import { AlertTriangle, Loader2, Plus } from 'lucide-react';
import { Button, Card, CardHeader } from '@/components/ui';
import { useToast } from '@/components/ui/Toast';
import {
  useOrgCredentials,
  useProviderCatalog,
  useSetDefaultCredential,
  useSpaceCredentials,
} from '@/hooks/useIdentityCredentials';
import AccountRow from '@/pages/credentials/AccountRow';
import AddApiKeyModal from '@/pages/credentials/AddApiKeyModal';
import { deriveProviderStatus } from '@/pages/credentials/identity-lib';
import type { Scope } from '@/pages/credentials/scope';
import { SLACK_IDENTITY_PROVIDER } from '@/lib/slack-integration';
import type { ProviderSummary } from '@/types/identity';

interface Props {
  /** Which credential store the bot tokens live in — org or space. */
  scope: Scope;
  /** Whether the caller may add / set-default / rename / revoke workspaces. */
  canManage: boolean;
}

/**
 * Slack bot-token management, rendered on the Slack integration page. Each
 * pasted workspace is a distinct api-key credential (keyed by team id) at the
 * integration's scope; this lists them, lets an admin add another, and — because
 * bot resolution needs a default when 2+ exist — surfaces a set-default
 * affordance. Org tokens store via `PUT /org/credentials/app`; space tokens via
 * `POST /spaces/{spaceId}/credentials/api-key` (routed inside AddApiKeyModal by
 * scope). The required bot scopes come from the identity catalog's
 * `apiKeySetupInstructions`, shown inside the add modal.
 */
export default function SlackBotCredentialsPanel({ scope, canManage }: Props) {
  const { error: showError } = useToast();
  const catalog = useProviderCatalog();
  const setDefault = useSetDefaultCredential();
  const [addModal, setAddModal] = useState(false);

  // Both queries are cheap and self-disable off-scope (org query is admin-only;
  // space query needs a spaceId), so calling them unconditionally is fine.
  const orgQuery = useOrgCredentials();
  const spaceQuery = useSpaceCredentials(
    scope.kind === 'space' ? scope.spaceId : undefined,
  );
  const credsQuery = scope.kind === 'space' ? spaceQuery : orgQuery;

  const slackEntry = catalog.data?.providers.find(
    (p) => p.id === SLACK_IDENTITY_PROVIDER,
  );

  const botCreds = (credsQuery.data?.credentials ?? []).filter(
    (c) => c.provider === SLACK_IDENTITY_PROVIDER && c.authType === 'api_key',
  );

  const hasDefault = botCreds.some((c) => c.metadata.isDefault);
  const needsDefaultPick = canManage && botCreds.length >= 2 && !hasDefault;

  // AddApiKeyModal expects a ProviderSummary; synthesize one from the catalog
  // entry (it only reads id / displayName / apiKeySetupInstructions / headers).
  const slackProvider: ProviderSummary | null = slackEntry
    ? {
        ...slackEntry,
        connectionCount: botCreds.length,
        status: deriveProviderStatus(botCreds, 'disconnected'),
      }
    : null;

  const pickFirstAsDefault = async () => {
    const first = botCreds[0];
    if (!first) return;
    try {
      await setDefault.mutateAsync(first.credId);
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Set-default failed');
    }
  };

  const loading = catalog.isLoading || credsQuery.isLoading || credsQuery.isPending;

  return (
    <Card>
      <CardHeader
        title="Slack workspaces"
        description="Bot tokens agents use to post as the Slack app. One token per workspace; set a default when more than one is connected."
      />

      {loading ? (
        <div className="flex items-center gap-2 text-muted-foreground py-6">
          <Loader2 className="h-4 w-4 animate-spin" />
          <span className="text-sm">Loading workspaces…</span>
        </div>
      ) : (
        <div className="space-y-3">
          {needsDefaultPick && (
            <div className="flex items-start gap-3 px-4 py-3 rounded-lg border border-status-warning-bg bg-status-warning-bg/30">
              <AlertTriangle className="h-4 w-4 text-status-warning-fg shrink-0 mt-0.5" />
              <div className="flex-1 text-sm">
                <p className="text-foreground font-medium">Pick a default workspace</p>
                <p className="text-muted-foreground text-xs mt-0.5">
                  Agents need to know which workspace's bot to post as. Choose one
                  as the default, or set it from any row's menu.
                </p>
              </div>
              <Button
                size="sm"
                variant="outline"
                onClick={pickFirstAsDefault}
                isLoading={setDefault.isPending}
              >
                Pick first
              </Button>
            </div>
          )}

          {botCreds.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              {canManage
                ? 'No workspace bot tokens yet. Add one to let agents post as the Slack app.'
                : 'No workspace bot tokens connected. Ask an admin to add one.'}
            </p>
          ) : (
            <div className="border border-border rounded-lg overflow-hidden bg-card divide-y divide-border">
              {botCreds.map((cred) => (
                <AccountRow
                  key={cred.credId}
                  credential={cred}
                  canManage={canManage}
                  hasSiblings={botCreds.length > 1}
                  scope={scope}
                />
              ))}
            </div>
          )}

          {canManage && slackProvider && (
            <div>
              <Button size="sm" variant="secondary" onClick={() => setAddModal(true)}>
                <Plus className="h-3.5 w-3.5 mr-1" />
                Add workspace
              </Button>
            </div>
          )}
        </div>
      )}

      {addModal && slackProvider && (
        <AddApiKeyModal
          isOpen={addModal}
          onClose={() => setAddModal(false)}
          provider={slackProvider}
          scope={scope}
        />
      )}
    </Card>
  );
}
